import { PropsWithChildren, ReactNode } from 'react';
import { Text, Status, InfoItem } from '@intellecteu/common-ui';
import cx from 'classnames';
import { Labels } from 'app/constants';
import {
  ApplicationDto,
  DomainDto,
  EnvVar,
  ParticipantDto,
  RemoteDomainDto,
  RemoteParticipantDto,
  ResourcesDto,
} from 'data';
import { correctStatus, generateStatusLabel } from 'ui';
import { ui } from 'ui/styles';
import { EnvVarsInfo } from '../../cards';

type TDomainInfoPhase =
  ParticipantDto.phase | RemoteParticipantDto.phase | DomainDto.phase | RemoteDomainDto.phase | ApplicationDto.phase;

interface DetailsInfoProps extends PropsWithChildren {
  name: string;
  phase?: TDomainInfoPhase;
  headerActions?: ReactNode;
  resources?: ResourcesDto;
  envVars?: EnvVar[];
  contentClassName?: string;
  hideCpuLimit?: boolean;
  secondRow?: ReactNode;
}

export const DetailsInfo = ({
  name,
  phase,
  headerActions,
  children,
  resources,
  envVars,
  contentClassName,
  hideCpuLimit = false,
  secondRow,
}: DetailsInfoProps) => {
  const resourcesBlock = resources && (
    <div className={ui.resourcesInfo}>
      <InfoItem label="Resources" labelWeight="bold" labelSize={Text.size.s} labelColor={Text.color.dark} />

      <ul className={ui.detailsList}>
        <InfoItem label={Labels.REQUESTED_CPU} value={resources?.cpuRequested} />

        {!hideCpuLimit && <InfoItem label={Labels.CPU_LIMIT} value={resources?.cpuLimit} />}

        <InfoItem label={Labels.REQUESTED_MEMORY} value={resources?.memoryRequested} />

        <InfoItem label={Labels.MEMORY_LIMIT} value={resources?.memoryLimit} />

        <InfoItem label={Labels.REPLICAS} value={String(resources?.replicas)} />
      </ul>
    </div>
  );

  const makeEnvVarsBlock = (flexGrow?: boolean) => (
    <div className={flexGrow ? ui.halfColumn : undefined}>
      {!!envVars?.length && (
        <EnvVarsInfo
          values={[
            {
              label: '',
              envVars,
            },
          ]}
        />
      )}
    </div>
  );

  return (
    <div className={ui.details}>
      <section className={ui.sectionWithDivider}>
        <div className={ui.detailsHeader}>
          <div className={ui.detailsHeaderText}>
            <Text size="xxl" weight="bold">
              {name}
            </Text>

            {phase && <Status mode={correctStatus(phase)}>{generateStatusLabel(phase)}</Status>}
          </div>

          {headerActions && <div className={ui.detailsHeaderActions}>{headerActions}</div>}
        </div>
      </section>

      <section className={cx(ui.detailsSection, ui.sectionWithDivider)}>
        <InfoItem
          label="Configurations"
          labelWeight="bold"
          labelSize={Text.size.m}
          labelColor={Text.color.dark}
          className={ui.detailsTitle}
        />

        {secondRow === undefined ? (
          <section className={cx(ui.detailsContent, contentClassName)} style={{ columnGap: '50px' }}>
            {children}
            {resourcesBlock}
            {makeEnvVarsBlock()}
          </section>
        ) : (
          <>
            <section className={cx(ui.detailsContent, contentClassName)} style={{ columnGap: '50px' }}>
              {children}
              {resourcesBlock}
            </section>
            <section className={cx(ui.detailsContent, contentClassName)} style={{ columnGap: '50px' }}>
              <div className={ui.halfColumn}>{secondRow}</div>
              {makeEnvVarsBlock(true)}
            </section>
          </>
        )}
      </section>
    </div>
  );
};
